import { useEffect, useMemo, useRef, useState } from 'react'
import { putFile } from '../../../utils/idbFiles'
import { fetchBlobFromUrl } from '../../../utils/upload'
import { useResolvedUrl } from '../../../utils/useResolvedUrl'
import { PinDetailsContent } from './PinDetailsContent'

function cleanTag(s) {
  return String(s || '')
    .trim()
    .replace(/^#+/, '')
    .slice(0, 24)
}

function uniqTags(list) {
  const out = []
  const seen = new Set()
  for (const t of Array.isArray(list) ? list : []) {
    const v = cleanTag(t)
    if (!v) continue
    const k = v.toLowerCase()
    if (seen.has(k)) continue
    seen.add(k)
    out.push(v)
  }
  return out
}

async function loadImageMeta(url) {
  const src = String(url || '').trim()
  if (!src) return { w: 0, h: 0 }
  let objUrl = ''
  let cleaned = src
  try {
    if (cleaned.startsWith('idb:')) {
      const blob = await fetchBlobFromUrl(cleaned)
      if (blob) {
        objUrl = URL.createObjectURL(blob)
        cleaned = objUrl
      }
    }
  } catch {}
  return await new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve({ w: img.naturalWidth || 0, h: img.naturalHeight || 0 })
    img.onerror = () => resolve({ w: 0, h: 0 })
    img.src = cleaned
  })
    .finally(() => {
      if (objUrl) {
        try {
          URL.revokeObjectURL(objUrl)
        } catch {}
      }
    })
}

async function fileToIdbUrl(file) {
  const f = file
  if (!f) return ''
  return await putFile(f, { name: f.name || 'file', type: f.type || '' })
}

async function captureVideoThumbnail(videoUrl, { atSec = 0.12, maxW = 720 } = {}) {
  const url = String(videoUrl || '').trim()
  if (!url) return { blob: null, width: 0, height: 0, durationMs: 0 }

  return await new Promise((resolve) => {
    const v = document.createElement('video')
    v.muted = true
    v.playsInline = true
    v.crossOrigin = 'anonymous'
    v.src = url

    const done = (payload) => resolve(payload)
    v.onerror = () => done({ blob: null, width: 0, height: 0, durationMs: 0 })

    v.onloadedmetadata = () => {
      const w = v.videoWidth || 0
      const h = v.videoHeight || 0
      const dur = Number.isFinite(v.duration) ? v.duration : 0
      const t = Math.max(0, Math.min(atSec, dur ? Math.max(0, dur - 0.05) : atSec))
      try {
        v.currentTime = t
      } catch {
        // ignore
      }

      v.onseeked = () => {
        const scale = w && h ? Math.min(1, maxW / w) : 1
        const outW = Math.max(120, Math.round(w * scale))
        const outH = Math.max(120, Math.round(h * scale))
        const canvas = document.createElement('canvas')
        canvas.width = outW
        canvas.height = outH
        const ctx = canvas.getContext('2d')
        if (!ctx) return done({ blob: null, width: w, height: h, durationMs: dur * 1000 })
        ctx.drawImage(v, 0, 0, outW, outH)
        canvas.toBlob((blob) => done({ blob: blob || null, width: w, height: h, durationMs: dur * 1000 }), 'image/jpeg', 0.88)
      }
    }
  })
}

export default function PinDetailsModal({
  open,
  pin,
  onClose,
  onSave,
  onDelete,
  onToggleLike,
  currentUser,
  allTags = [],
}) {
  const p = pin && typeof pin === 'object' ? pin : null
  const canShow = Boolean(open && p)

  const userId = String(currentUser?.id || currentUser?.email || '').trim()

  const [edit, setEdit] = useState(false)
  const [title, setTitle] = useState('')
  const [visibility, setVisibility] = useState('private')
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [busy, setBusy] = useState(false)

  const [quality, setQuality] = useState(0.9)
  const [maxSize, setMaxSize] = useState(1080)

  const cropperRef = useRef(null)
  const replaceInputRef = useRef(null)
  const videoRef = useRef(null)
  const trimStopRef = useRef({ stop: null })

  const isVideo = p?.media?.kind === 'video'
  const mediaUrlRaw = String(p?.media?.url || '').trim()
  const thumbUrlRaw = String(p?.media?.thumbnailUrl || '').trim() || mediaUrlRaw
  const mediaUrl = useResolvedUrl(mediaUrlRaw)
  const thumbUrl = useResolvedUrl(thumbUrlRaw)
  const likes = Array.isArray(p?.likes) ? p.likes : []
  const liked = Boolean(userId && likes.includes(userId))

  const [videoDuration, setVideoDuration] = useState(0)
  const [trimStart, setTrimStart] = useState(0)
  const [trimEnd, setTrimEnd] = useState(0)

  const suggestedTags = useMemo(() => {
    const q = String(tagInput || '').trim().toLowerCase()
    const list = Array.isArray(allTags) ? allTags : []
    if (!q) return list.filter((t) => !tags.includes(t)).slice(0, 8)
    return list
      .filter((t) => String(t).toLowerCase().includes(q) && !tags.includes(t))
      .slice(0, 8)
  }, [allTags, tagInput, tags])

  useEffect(() => {
    if (!canShow) return
    setEdit(false)
    setTitle(String(p?.title || 'Untitled'))
    setVisibility(p?.visibility === 'public' ? 'public' : 'private')
    setTags(uniqTags(p?.tags || []))
    setTagInput('')
    setBusy(false)
    setQuality(0.9)
    setMaxSize(1080)
    setVideoDuration(0)
    setTrimStart(0)
    setTrimEnd(0)
  }, [canShow, p])

  const addTag = (raw) => {
    const v = cleanTag(raw)
    if (!v) return
    setTags((prev) => uniqTags([...prev, v]))
    setTagInput('')
  }

  const removeTag = (t) => setTags((prev) => prev.filter((x) => x !== t))

  const doReplaceMedia = async (file) => {
    const f = file
    if (!f) return
    setBusy(true)
    try {
      const url = await fileToIdbUrl(f)
      if (!url) throw new Error('replace_failed')

      if (f.type.startsWith('video/')) {
        const objUrl = URL.createObjectURL(f)
        const { blob, width, height, durationMs } = await captureVideoThumbnail(objUrl, { atSec: 0.12 })
        URL.revokeObjectURL(objUrl)
        const thumb = blob ? await putFile(blob, { name: 'thumb.jpg', type: 'image/jpeg' }) : ''
        onSave({
          ...p,
          title: String(title || '').trim() || 'Untitled',
          tags,
          visibility,
          media: {
            ...p.media,
            kind: 'video',
            url,
            thumbnailUrl: thumb,
            mime: f.type,
            sizeBytes: f.size || 0,
            width,
            height,
            durationMs,
          },
        })
        return
      }

      const { w, h } = await loadImageMeta(url)
      onSave({
        ...p,
        title: String(title || '').trim() || 'Untitled',
        tags,
        visibility,
        media: {
          ...p.media,
          kind: 'image',
          url,
          thumbnailUrl: url,
          mime: f.type,
          sizeBytes: f.size || 0,
          width: w,
          height: h,
          durationMs: 0,
        },
      })
    } catch {
      // ignore
    } finally {
      setBusy(false)
    }
  }

  const doSave = async () => {
    if (!p) return
    if (!edit) return
    setBusy(true)
    try {
      const nextTitle = String(title || '').trim() || 'Untitled'

      if (!isVideo) {
        const exported = await cropperRef.current?.exportBlob?.({ maxSize, quality, mime: 'image/jpeg' })
        if (exported) {
          const url = await putFile(exported, { name: 'image.jpg', type: exported.type || 'image/jpeg' })
          const { w, h } = await loadImageMeta(url)
          onSave({
            ...p,
            title: nextTitle,
            tags,
            visibility,
            media: {
              ...p.media,
              kind: 'image',
              url,
              thumbnailUrl: url,
              mime: exported.type || 'image/jpeg',
              sizeBytes: exported.size || 0,
              width: w,
              height: h,
              durationMs: 0,
            },
          })
          setEdit(false)
          return
        }
      }

      onSave({
        ...p,
        title: nextTitle,
        tags,
        visibility,
        updatedAt: Date.now(),
      })
      setEdit(false)
    } finally {
      setBusy(false)
    }
  }

  const stopTrimPreview = () => {
    try {
      trimStopRef.current?.stop?.()
    } catch {}
    trimStopRef.current = { stop: null }
  }

  const doTrimVideo = async () => {
    if (!p || !isVideo) return
    const videoEl = videoRef.current
    if (!videoEl) return

    const startSec = Math.max(0, Number(trimStart) || 0)
    const endSec = Math.max(0, Number(trimEnd) || 0)
    if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec + 0.05) return

    const capture = videoEl.captureStream || videoEl.mozCaptureStream
    if (typeof capture !== 'function' || typeof window === 'undefined' || typeof window.MediaRecorder !== 'function') {
      return
    }

    const pickMime = () => {
      const prefs = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
      ]
      for (const t of prefs) {
        try {
          if (window.MediaRecorder.isTypeSupported(t)) return t
        } catch {}
      }
      return ''
    }

    stopTrimPreview()
    setBusy(true)
    try {
      const mimeType = pickMime()
      const stream = capture.call(videoEl)
      const recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      const chunks = []

      const waitStopped = new Promise((resolve, reject) => {
        recorder.onstop = () => resolve()
        recorder.onerror = (e) => reject(e)
      })

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size) chunks.push(e.data)
      }

      const origMuted = videoEl.muted
      videoEl.muted = true

      const seekTo = (t) =>
        new Promise((resolve) => {
          const handler = () => {
            videoEl.removeEventListener('seeked', handler)
            resolve()
          }
          videoEl.addEventListener('seeked', handler, { once: true })
          try {
            videoEl.currentTime = t
          } catch {
            videoEl.removeEventListener('seeked', handler)
            resolve()
          }
        })

      await seekTo(startSec)

      const stop = () => {
        ticking = false
        try {
          if (recorder.state !== 'inactive') recorder.stop()
        } catch {}
        try {
          videoEl.pause()
        } catch {}
        try {
          videoEl.muted = origMuted
        } catch {}
      }

      trimStopRef.current = { stop }

      let ticking = true
      const tick = () => {
        if (!ticking) return
        try {
          if (videoEl.currentTime >= endSec - 0.05) {
            ticking = false
            stop()
            return
          }
        } catch {}
        requestAnimationFrame(tick)
      }

      await videoEl.play().catch(() => null)
      recorder.start(220)
      requestAnimationFrame(tick)

      // hard timeout safety
      const maxMs = Math.max(200, Math.min(60_000, Math.round((endSec - startSec) * 1000) + 1500))
      const timeout = setTimeout(() => stop(), maxMs)
      await waitStopped.finally(() => clearTimeout(timeout))

      const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' })
      if (!blob.size) return

      const objUrl = URL.createObjectURL(blob)
      const { blob: thumbBlob, width, height, durationMs } = await captureVideoThumbnail(objUrl, { atSec: 0.12 })
      try {
        URL.revokeObjectURL(objUrl)
      } catch {}

      const url = await putFile(blob, { name: 'trimmed.webm', type: blob.type || 'video/webm' })
      const thumb = thumbBlob ? await putFile(thumbBlob, { name: 'thumb.jpg', type: 'image/jpeg' }) : ''

      const nextTitle = String(title || '').trim() || 'Untitled'
      onSave({
        ...p,
        title: nextTitle,
        tags,
        visibility,
        updatedAt: Date.now(),
        media: {
          ...p.media,
          kind: 'video',
          url,
          thumbnailUrl: thumb,
          mime: blob.type || 'video/webm',
          sizeBytes: blob.size || 0,
          width,
          height,
          durationMs: durationMs || Math.round((endSec - startSec) * 1000),
        },
      })
      setEdit(false)
    } finally {
      setBusy(false)
      trimStopRef.current = { stop: null }
    }
  }

  return (
    <PinDetailsContent
      open={open}
      onClose={onClose}
      edit={edit}
      setEdit={setEdit}
      busy={busy}
      onToggleLike={onToggleLike}
      pin={p}
      userId={userId}
      liked={liked}
      likes={likes}
      replaceInputRef={replaceInputRef}
      doReplaceMedia={doReplaceMedia}
      onDelete={onDelete}
      doSave={doSave}
      isVideo={isVideo}
      mediaUrl={mediaUrl}
      thumbUrl={thumbUrl}
      title={title}
      setTitle={setTitle}
      visibility={visibility}
      setVisibility={setVisibility}
      tags={tags}
      removeTag={removeTag}
      tagInput={tagInput}
      setTagInput={setTagInput}
      addTag={addTag}
      suggestedTags={suggestedTags}
      cropperRef={cropperRef}
      maxSize={maxSize}
      setMaxSize={setMaxSize}
      quality={quality}
      setQuality={setQuality}
      videoRef={videoRef}
      setVideoDuration={setVideoDuration}
      trimStart={trimStart}
      setTrimStart={setTrimStart}
      trimEnd={trimEnd}
      setTrimEnd={setTrimEnd}
      videoDuration={videoDuration}
      doTrimVideo={doTrimVideo}
      stopTrimPreview={stopTrimPreview}
    />
  )
}
